import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { API, api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { track } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Copy, Sparkles, Lock } from "lucide-react";

const GOALS = [
  { v: "counter", label: "Counter-offer" },
  { v: "accept", label: "Accept" },
  { v: "decline", label: "Politely decline" },
  { v: "clarify", label: "Ask clarifying qs" },
];

export default function Copilot() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const inboxId = params.get("inbox_id");
  const [brandEmail, setBrandEmail] = useState("");
  const [goal, setGoal] = useState("counter");
  const [myRate, setMyRate] = useState("");
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [pitchMeta, setPitchMeta] = useState(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (!inboxId) return;
    api
      .get(`/inbox/${inboxId}`)
      .then((r) => {
        const p = r.data;
        setPitchMeta({ from: p.from_name || p.from_email, subject: p.subject });
        const header = [p.from_name && `From: ${p.from_name} <${p.from_email}>`, p.subject && `Subject: ${p.subject}`]
          .filter(Boolean)
          .join("\n");
        setBrandEmail(header ? `${header}\n\n${p.body}` : p.body);
      })
      .catch(() => toast.error("Could not load pitch."));
  }, [inboxId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!brandEmail.trim()) return toast.error("Paste the brand email first.");
    setBusy(true);
    setReply("");

    const token = localStorage.getItem("dap_token");
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const resp = await fetch(`${API}/copilot/reply`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          brand_email: brandEmail,
          goal,
          my_rate: myRate ? Number(myRate) : null,
        }),
        signal: controller.signal,
      });
      if (!resp.ok) {
        const t = await resp.text();
        throw new Error(t || "Stream failed");
      }
      const reader = resp.body.getReader();
      const dec = new TextDecoder();
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        setReply((prev) => prev + dec.decode(value));
      }
    } catch (err) {
      if (err.name !== "AbortError") toast.error(err.message || "Stream failed");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(reply);
      toast.success("Copied to clipboard.");
    } catch {
      toast.error("Copy failed — select and copy manually.");
    }
    if (inboxId) {
      try {
        await api.patch(`/inbox/${inboxId}`, { replied: true });
      } catch {}
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-12">
      <p className="font-mono text-xs uppercase tracking-widest text-[#CCFF00] mb-3">// deal copilot</p>
      <h1 className="font-display font-black text-5xl md:text-6xl tracking-tighter mb-3">
        Reply like you have an agent.
      </h1>
      <p className="text-neutral-400 mb-10 max-w-2xl">
        Paste the brand's pitch email. Pick your move. Claude drafts a warm, confident reply — streaming, so
        you can read as it writes.
      </p>

      {pitchMeta && (
        <div className="mb-6 p-4 rounded-xl bg-[#141414] border border-white/5" data-testid="copilot-pitch-context">
          <p className="text-xs font-mono uppercase tracking-widest text-[#CCFF00]">// replying to</p>
          <p className="mt-1 font-display font-bold text-lg">{pitchMeta.subject}</p>
          <p className="text-sm text-neutral-400">{pitchMeta.from}</p>
        </div>
      )}

      {user?.plan === "free" && (
        <div className="mb-8 p-6 rounded-2xl bg-[#CCFF00]/10 border border-[#CCFF00]/30 flex items-center justify-between gap-4 flex-wrap" data-testid="copilot-paywall">
          <div className="flex items-center gap-3">
            <Lock className="w-5 h-5 text-[#CCFF00] shrink-0" />
            <div>
              <p className="font-display font-bold">Deal Copilot is a Pro feature.</p>
              <p className="text-sm text-neutral-400">$29/mo. Cancel anytime. Test cards work in sandbox.</p>
            </div>
          </div>
          <Link
            to="/billing"
            data-testid="copilot-upgrade-link"
            onClick={() => track("dap_paywall_upgrade_clicked", { source: "copilot" })}
          >
            <Button className="rounded-full bg-[#CCFF00] text-black hover:bg-[#b8e600] font-semibold">
              Upgrade to Pro <Sparkles className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <form onSubmit={submit} className="space-y-4" data-testid="copilot-form">
          <Field label="Brand's email">
            <Textarea
              rows={12}
              value={brandEmail}
              onChange={(e) => setBrandEmail(e.target.value)}
              placeholder="Hey [creator], we'd love to work with you on our summer campaign…"
              className="bg-[#0e0e0e] border-white/10 focus-visible:ring-[#CCFF00]"
              data-testid="copilot-brand-email-textarea"
            />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Your goal">
              <Select value={goal} onValueChange={setGoal}>
                <SelectTrigger className="h-12 bg-[#0e0e0e] border-white/10" data-testid="copilot-goal-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GOALS.map((g) => (
                    <SelectItem key={g.v} value={g.v}>
                      {g.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Your target rate ($ USD)">
              <Input
                type="number"
                value={myRate}
                onChange={(e) => setMyRate(e.target.value)}
                placeholder="1500"
                className="h-12 bg-[#0e0e0e] border-white/10 font-mono"
                data-testid="copilot-rate-input"
              />
            </Field>
          </div>
          <Button
            type="submit"
            disabled={busy}
            className="w-full h-12 rounded-full bg-[#CCFF00] text-black hover:bg-[#b8e600] font-semibold"
            data-testid="copilot-submit-btn"
          >
            {busy ? "Drafting…" : "Draft my reply"} <Sparkles className="w-4 h-4" />
          </Button>
        </form>

        <div className="p-6 rounded-2xl bg-[#141414] border border-white/5 min-h-[400px] flex flex-col" data-testid="copilot-reply-panel">
          <div className="flex items-center justify-between mb-4">
            <p className="font-mono text-xs uppercase tracking-widest text-[#CCFF00]">// your draft</p>
            {reply && (
              <Button variant="ghost" size="sm" onClick={copy} data-testid="copilot-copy-btn">
                <Copy className="w-3 h-3" /> Copy
              </Button>
            )}
          </div>
          <div className={`whitespace-pre-wrap text-neutral-100 leading-relaxed text-sm flex-1 ${busy && "caret"}`} data-testid="copilot-reply-text">
            {reply || (
              <span className="text-neutral-500">Your streaming reply appears here.</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-widest font-mono text-neutral-500 mb-1 block">{label}</span>
    {children}
  </label>
);
